import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Database, GraduationCap } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProgressBar, StatusPill } from "@/components/StatusPill";
import { LearnerUnit } from "@/components/LearnerUnit";
import { TrainingProgrammes } from "@/components/TrainingProgrammes";
import { ExportButton } from "@/components/ExportButton";
import { SortBar, SortHeader } from "@/components/SortHeader";
import { sortRows, useSort } from "@/lib/sorting";
import { api, fmtDay, fmtWhen, PROGRESS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Tile = ({ label, value, sub, testId, onClick, colour }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className="bg-card p-4 text-left transition-colors duration-200 hover:bg-muted"
    style={colour ? { borderTop: `3px solid ${colour}` } : undefined}
  >
    <div className="label-caps">{label}</div>
    <div className="font-data mt-2 text-2xl font-bold">{value}</div>
    <div className="text-[11px] text-muted-foreground">{sub}</div>
  </button>
);

export default function Training() {
  const { isAdmin, scopedPlant, shows } = useAuth();
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [plant, setPlant] = useState(scopedPlant || "all");
  const [progress, setProgress] = useState("all");
  const [path, setPath] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [beat, setBeat] = useState(0);
  const { sort, on: sortBy } = useSort("training-learners");
  const units = useSort("training-units");
  const paths = useSort("training-paths");

  const load = () => {
    api.get("/training/summary").then((r) => setSummary(r.data));
  };

  useEffect(load, []);

  useEffect(() => {
    const t = setTimeout(() => {
      api
        .get("/training/learners", {
          params: {
            plant: plant === "all" ? "" : plant,
            progress: progress === "all" ? "" : progress,
            path: path === "all" ? "" : path,
            q,
          },
        })
        .then((r) => setRows(r.data));
    }, 200);
    return () => clearTimeout(t);
  }, [plant, progress, path, q, beat]);

  const o = summary?.overall;
  // SDTD moves any learner; a unit's own officers move theirs. The server has the final word.
  const unitCodes = (summary?.groups || []).flatMap((g) => (g.units || []).map((u) => u.plant));
  const canMoveLearner = isAdmin || !!scopedPlant;

  return (
    <div className="p-5 sm:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps">Coursera training</div>
          <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl">
            Training status
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Where our officers stand on their Coursera courses. Click any unit or learner to see
            the details.
          </p>
        </div>
        {isAdmin && (
          <div className="flex flex-col items-end gap-1">
            <Link to="/data" data-testid="go-to-data-files">
              <Button className="h-11 rounded-none font-display text-xs font-bold uppercase tracking-widest">
                <Database className="mr-2 h-4 w-4" />
                Upload latest tracker
              </Button>
            </Link>
            <span className="text-[11px] text-muted-foreground">
              Opens Data files — you see the difference before anything is saved.
            </span>
          </div>
        )}
      </div>

      {summary?.upload?.file_name && (
        <p className="font-data mt-4 border border-border bg-card px-4 py-2 text-[11px] text-muted-foreground">
          Last uploaded: {summary.upload.file_name} (sheet "{summary.upload.sheet_used}") ·{" "}
          {summary.upload.learners} learners · by {summary.upload.uploaded_by} on{" "}
          {fmtWhen(summary.upload.uploaded_at)}
        </p>
      )}

      <div className="mt-6 grid grid-cols-2 gap-px bg-border lg:grid-cols-6">
        <Tile testId="training-total" label="Officers enrolled" value={o?.total ?? 0} sub="On Coursera" />
        <Tile
          testId="training-finished"
          label="Finished courses"
          value={o?.finished ?? 0}
          sub="All assigned courses done"
          colour={PROGRESS.on_track.color}
        />
        <Tile
          testId="training-on-track"
          label="On Track"
          value={o?.on_track ?? 0}
          sub="Going well"
          colour={PROGRESS.on_track.color}
          onClick={() => setProgress("on_track")}
        />
        <Tile
          testId="training-attention"
          label="Needs Attention"
          value={o?.needs_attention ?? 0}
          sub="Slow progress"
          colour={PROGRESS.needs_attention.color}
          onClick={() => setProgress("needs_attention")}
        />
        <Tile
          testId="training-delayed"
          label="Delayed"
          value={o?.delayed ?? 0}
          sub="No progress / inactive"
          colour={PROGRESS.delayed.color}
          onClick={() => setProgress("delayed")}
        />
        <Tile
          testId="training-courses"
          label="Courses done"
          value={`${o?.courses_done ?? 0}/${o?.courses_assigned ?? 0}`}
          sub={`Average ${o?.avg_completion ?? 0}% complete`}
        />
      </div>

      {shows("training.programmes") && <TrainingProgrammes />}

      {shows("training.units") && (
      <div className="mt-8 border border-border bg-card">
        <div className="border-b border-border p-5">
          <div className="label-caps">Unit by unit</div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            Click a unit to see its learners
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Grouped as on the leaderboard — plants, central units and mines. Inside each group the
            unit that has covered the most ground stands first.
          </p>
          <div className="mt-3">
            <SortBar
              testId="training-units-sort"
              sort={units.sort}
              onSort={units.on}
              note="within each group"
              columns={[
                ["plant", "Unit"],
                ["total", "On Coursera"],
                ["finished", "Finished"],
                ["avg_completion", "Average %"],
              ]}
            />
          </div>
        </div>
        {summary?.groups?.map((g) => (
          <div key={g.group} data-testid={`training-group-${g.group}`}>
            <div className="flex flex-wrap items-baseline justify-between gap-2 border-y border-border bg-muted px-5 py-2">
              <span className="font-display text-xs font-bold uppercase tracking-widest">
                {g.group}
              </span>
              <span className="font-data text-[11px] text-muted-foreground">
                {g.units.length} unit(s) · {g.total} on Coursera · {g.finished} finished ·{" "}
                {g.avg_completion}% average
              </span>
            </div>
            <div className="divide-y divide-border">
              {sortRows(g.units, units.sort.key, units.sort.dir).map((u) => (
                <button
                  key={u.plant}
                  type="button"
                  data-testid={`training-plant-${u.plant}`}
                  onClick={() => setPlant(u.plant)}
                  disabled={!u.total}
                  className={`w-full border-l-2 px-5 py-4 text-left transition-colors duration-200 ${
                    u.total ? "hover:bg-muted" : "cursor-default opacity-60"
                  } ${plant === u.plant ? "border-primary bg-muted" : "border-transparent"}`}
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="text-sm font-semibold">
                      {u.plant}{" "}
                      <span className="font-normal text-muted-foreground">— {u.plant_name}</span>
                    </span>
                    <span className="font-data text-xs">
                      {u.total
                        ? `${u.finished}/${u.total} finished · ${u.avg_completion}% average`
                        : "nobody on Coursera yet"}
                    </span>
                  </div>
                  {u.total > 0 && (
                    <>
                      <div className="mt-2 flex h-3 w-full overflow-hidden bg-muted">
                        {["on_track", "needs_attention", "delayed"].map((k) => (
                          <div
                            key={k}
                            style={{
                              width: `${u.total ? (u[k] / u.total) * 100 : 0}%`,
                              backgroundColor: PROGRESS[k].color,
                            }}
                            title={`${PROGRESS[k].label}: ${u[k]}`}
                          />
                        ))}
                      </div>
                      <div className="mt-1.5 text-[11px] text-muted-foreground">
                        {u.on_track} on track · {u.needs_attention} need attention · {u.delayed}{" "}
                        delayed · {u.not_started} yet to start
                      </div>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      )}

      {shows("training.paths") && (
      <div className="mt-8 border border-border bg-card" data-testid="training-paths">
        <div className="border-b border-border p-5">
          <div className="label-caps">Path by path</div>
          <h3 className="font-display text-xl font-bold tracking-tight">
            How each learning path is faring
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            The path that has covered the most ground first, so a Data Engineer's progress sits
            beside a Delivery Lead's. Click a path to see only those officers.
          </p>
          <div className="mt-3">
            <SortBar
              testId="training-paths-sort"
              sort={paths.sort}
              onSort={paths.on}
              columns={[
                ["path", "Path"],
                ["total", "Officers"],
                ["finished", "Finished"],
                ["avg_completion", "Average %"],
                ["courses_done", "Courses done"],
              ]}
            />
          </div>
        </div>
        <div className="divide-y divide-border">
          {sortRows(summary?.by_path || [], paths.sort.key, paths.sort.dir).map((p) => (
            <button
              key={p.path}
              type="button"
              data-testid={`training-path-${p.path}`}
              onClick={() => setPath(path === p.path ? "all" : p.path)}
              className={`w-full border-l-2 px-5 py-4 text-left transition-colors duration-200 hover:bg-muted ${
                path === p.path ? "border-primary bg-muted" : "border-transparent"
              }`}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-semibold">
                  {p.path}{" "}
                  <span className="font-normal text-muted-foreground">
                    — {p.total} officer(s)
                  </span>
                </span>
                <span className="font-data text-xs">
                  {p.finished}/{p.total} finished · {p.avg_completion}% average ·{" "}
                  {p.courses_done}/{p.courses_assigned} courses
                </span>
              </div>
              <div className="mt-2 flex h-3 w-full overflow-hidden bg-muted">
                {["on_track", "needs_attention", "delayed"].map((k) => (
                  <div
                    key={k}
                    style={{
                      width: `${p.total ? (p[k] / p.total) * 100 : 0}%`,
                      backgroundColor: PROGRESS[k].color,
                    }}
                    title={`${PROGRESS[k].label}: ${p[k]}`}
                  />
                ))}
              </div>
              <div className="mt-1.5 text-[11px] text-muted-foreground">
                {p.on_track} on track · {p.needs_attention} need attention · {p.delayed} delayed ·{" "}
                {p.not_started} yet to start
              </div>
            </button>
          ))}
        </div>
      </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <Input
          data-testid="learner-search"
          placeholder="Search a learner by name, email or department…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-11 min-w-[240px] flex-1 rounded-none bg-card"
        />
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-11 w-[200px] rounded-none bg-card" data-testid="learner-filter-plant">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            {!scopedPlant && <SelectItem value="all">All units</SelectItem>}
            {summary?.groups?.map((g) => (
              <SelectGroup key={g.group}>
                <SelectLabel className="label-caps text-[10px]">{g.group}</SelectLabel>
                {g.units.map((u) => (
                  <SelectItem key={u.plant} value={u.plant}>
                    {u.plant}
                    <span className="text-muted-foreground">
                      {u.total ? ` · ${u.avg_completion}%` : " · nobody yet"}
                    </span>
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
        <Select value={progress} onValueChange={setProgress}>
          <SelectTrigger className="h-11 w-[180px] rounded-none bg-card" data-testid="learner-filter-progress">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Everyone</SelectItem>
            <SelectItem value="on_track">On Track</SelectItem>
            <SelectItem value="needs_attention">Needs Attention</SelectItem>
            <SelectItem value="delayed">Delayed</SelectItem>
          </SelectContent>
        </Select>
        <Select value={path} onValueChange={setPath}>
          <SelectTrigger className="h-11 w-[220px] rounded-none bg-card" data-testid="learner-filter-path">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-popover">
            <SelectItem value="all">Every learning path</SelectItem>
            {summary?.by_path?.map((p) => (
              <SelectItem key={p.path} value={p.path}>
                {p.path}
                <span className="text-muted-foreground"> · {p.avg_completion}%</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {shows("training.export") && (
          <ExportButton
            path="/training/learners/export"
            params={{
              plant: plant === "all" ? "" : plant,
              progress: progress === "all" ? "" : progress,
              path: path === "all" ? "" : path,
              q,
            }}
            testId="export-learners"
            label="Download this view"
          />
        )}
      </div>

      {shows("training.learners") && (
      <div className="mt-4 overflow-x-auto border border-border bg-card">
        <table className="w-full min-w-[900px] text-sm" data-testid="learners-table">
          <thead className="bg-muted">
            <tr className="text-left">
              {[
                ["name", "Officer"],
                ["plant", "Unit"],
                ["learning_path", "Learning path"],
                ["completed_courses", "Courses"],
                ["completion_pct", "Progress"],
                ["progress", "Status"],
                ["last_activity_at", "Last activity"],
              ].map(([column, label]) => (
                <th key={column} className="label-caps px-4 py-3 font-bold">
                  <SortHeader label={label} column={column} sort={sort} onSort={sortBy} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortRows(rows, sort.key, sort.dir, (r, key) =>
              key === "progress"
                ? { delayed: 0, needs_attention: 1, not_started: 2, on_track: 3 }[r.progress] ?? 9
                : r[key],
            ).map((r) => (
              <tr
                key={r.email}
                data-testid={`learner-row-${r.email}`}
                className="cursor-pointer transition-colors duration-200 hover:bg-muted"
                onClick={() => setOpen(r)}
              >
                <td className="px-4 py-3">
                  <div className="font-semibold">{r.name}</div>
                  <div className="font-data text-[11px] text-muted-foreground">{r.email}</div>
                </td>
                <td className="px-4 py-3 text-xs" onClick={(e) => e.stopPropagation()}>
                  <LearnerUnit
                    learner={r}
                    units={unitCodes}
                    canEdit={canMoveLearner}
                    onMoved={() => {
                      setBeat((b) => b + 1);
                      load();
                    }}
                  />
                </td>
                <td className="px-4 py-3 text-xs">{r.learning_path || "—"}</td>
                <td className="font-data px-4 py-3 text-xs">
                  {r.completed_courses}/{r.assigned_courses}
                </td>
                <td className="w-[180px] px-4 py-3">
                  <ProgressBar value={r.completion_pct} color={PROGRESS[r.progress].color} />
                  <div className="font-data mt-1 text-[11px]">{Math.round(r.completion_pct)}%</div>
                </td>
                <td className="px-4 py-3">
                  <StatusPill
                    progress={r.progress}
                    compact
                    testId={`learner-status-${r.email}`}
                    onClick={() => setOpen(r)}
                  />
                </td>
                <td className="font-data px-4 py-3 text-xs">
                  {r.last_activity_at ? fmtDay(r.last_activity_at.slice(0, 10)) : "—"}
                </td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={7} className="p-8 text-center text-muted-foreground">
                  No learner matches what you selected.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      <Sheet open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <SheetContent
          className="w-full overflow-y-auto border-l bg-background sm:max-w-lg"
          data-testid="learner-detail-sheet"
        >
          <SheetHeader className="text-left">
            <div className="label-caps">Coursera learner</div>
            <SheetTitle className="font-display text-2xl font-bold tracking-tight">
              {open?.name}
            </SheetTitle>
            <SheetDescription>
              {open?.email} · {open?.plant} · {open?.grade || "—"}
            </SheetDescription>
          </SheetHeader>
          {open && (
            <div className="mt-6 space-y-6">
              <div className="border border-border bg-card p-5">
                <div className="flex items-center gap-3">
                  <GraduationCap className="h-5 w-5" style={{ color: PROGRESS[open.progress].color }} />
                  <span
                    className="font-display text-xl font-bold"
                    style={{ color: PROGRESS[open.progress].color }}
                  >
                    {open.progress_label}
                  </span>
                </div>
                <div className="mt-4">
                  <ProgressBar
                    value={open.completion_pct}
                    color={PROGRESS[open.progress].color}
                    height="h-3"
                  />
                  <div className="font-data mt-2 text-sm">
                    {open.completed_courses} of {open.assigned_courses} courses done (
                    {Math.round(open.completion_pct)}%)
                  </div>
                </div>
                <ul className="mt-4 space-y-1.5 text-sm">
                  {open.reasons?.map((r) => (
                    <li key={r} className="flex gap-2">
                      <span style={{ color: PROGRESS[open.progress].color }}>•</span>
                      {r}
                    </li>
                  ))}
                </ul>
                <div className="mt-4 border-t border-border pt-3 text-sm">
                  <span className="label-caps">What next</span>
                  <p className="mt-1">{open.next_step}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-px bg-border">
                {[
                  ["Learning path", open.learning_path],
                  ["Tranche", open.tranche],
                  ["Department", open.department],
                  ["SAIL ID", open.sail_id],
                  ["Programme", open.programme],
                  ["Contract", open.contract],
                  ["Joined", open.join_date ? fmtDay(open.join_date.slice(0, 10)) : null],
                  [
                    "Last activity",
                    open.last_activity_at
                      ? `${fmtDay(open.last_activity_at.slice(0, 10))} (${open.days_since_activity} days ago)`
                      : null,
                  ],
                ].map(([label, value]) => (
                  <div key={label} className="bg-card p-3">
                    <div className="label-caps">{label}</div>
                    <div className="mt-1 text-xs font-medium">{value || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
